const VCARD_PATTERN = /BEGIN:VCARD(?:\r\n|\n|\r)[\s\S]*?END:VCARD/gi;
const SEAM_CALL_PREFIX = 'SEAM_CHAT_CALL:';
const LEGACY_UNSUPPORTED_CALL_PATTERN =
  /(bu mesaj türü bu gelen kutusunda desteklenmiyor|this message type is not supported in this inbox)/i;
const HISTORY_PULL_PATTERN = /messages\.pull/i;

function unfoldLines(value) {
  return value
    .replace(/=\r?\n/g, '')
    .replace(/\r\n[ \t]/g, '')
    .replace(/\n[ \t]/g, '');
}

function decodeQuotedPrintable(value) {
  return value.replace(/=([0-9A-F]{2})/gi, (_, hex) =>
    String.fromCharCode(Number.parseInt(hex, 16))
  );
}

function decodeValue(value, parameters = '') {
  let decoded = value;
  if (/ENCODING=QUOTED-PRINTABLE/i.test(parameters)) {
    decoded = decodeQuotedPrintable(decoded);
  }
  return decoded
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\')
    .trim();
}

function propertyName(metadata) {
  return metadata.split(';')[0].split('.').at(-1)?.toUpperCase() || '';
}

function parameterValue(metadata, name) {
  const match = metadata.match(new RegExp(`(?:^|;)${name}=([^;:]*)`, 'i'));
  return match?.[1]?.replace(/^"|"$/g, '') || '';
}

function displayNameFromStructuredName(value) {
  const [family, given, additional, prefix, suffix] = value.split(';');
  return [prefix, given, additional, family, suffix]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

function phoneDetails(metadata, value) {
  const waid = parameterValue(metadata, 'waid');
  const display = value.replace(/^tel:/i, '').trim();
  const digits = (waid || display).replace(/\D/g, '');
  const type = parameterValue(metadata, 'type')
    .split(',')
    .map(item => item.trim().toLowerCase())
    .find(item => item && item !== 'voice');

  return {
    display: display || (digits ? `+${digits}` : ''),
    normalized: digits ? `+${digits}` : '',
    digits,
    type: type || '',
  };
}

function parseBlock(raw) {
  const lines = unfoldLines(raw).split(/\r\n|\n|\r/);
  const card = {
    name: '',
    organization: '',
    title: '',
    phones: [],
    emails: [],
    raw: raw.replace(/\r?\n|\r/g, '\r\n'),
  };
  let structuredName = '';

  for (const line of lines) {
    const separator = line.indexOf(':');
    if (separator < 0) continue;

    const metadata = line.slice(0, separator);
    const name = propertyName(metadata);
    const value = decodeValue(line.slice(separator + 1), metadata);

    if (name === 'FN') card.name = value;
    if (name === 'N') structuredName = displayNameFromStructuredName(value);
    if (name === 'ORG') card.organization = value.split(';').filter(Boolean).join(' · ');
    if (name === 'TITLE') card.title = value;
    if (name === 'TEL') {
      const phone = phoneDetails(metadata, value);
      if (phone.digits && !card.phones.some(item => item.digits === phone.digits)) {
        card.phones.push(phone);
      }
    }
    if (name === 'EMAIL' && value) {
      const normalized = value.replace(/^mailto:/i, '').trim();
      if (!card.emails.includes(normalized)) card.emails.push(normalized);
    }
  }

  card.name = card.name || structuredName || card.phones[0]?.display || 'WhatsApp contact';
  return card;
}

export function parseVCardContent(content) {
  if (typeof content !== 'string' || !/BEGIN:VCARD/i.test(content)) return [];
  const blocks = content.match(VCARD_PATTERN) || [];
  return blocks.map(parseBlock).filter(card => card.phones.length || card.emails.length);
}

export function vCardFilename(name) {
  const safeName = String(name || 'whatsapp-contact')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `${safeName || 'whatsapp-contact'}.vcf`;
}

export function parseSeamCallContent(content) {
  if (typeof content !== 'string' || !content.startsWith(SEAM_CALL_PREFIX)) {
    return null;
  }

  try {
    const call = JSON.parse(content.slice(SEAM_CALL_PREFIX.length));
    const kinds = new Set(['received', 'accepted', 'missed', 'incoming', 'outgoing']);
    if (!call || !kinds.has(call.kind)) return null;

    return {
      kind: call.kind,
      video: Boolean(call.video),
      duration: Number.isFinite(Number(call.duration))
        ? Math.max(0, Number(call.duration))
        : null,
    };
  } catch {
    return null;
  }
}

// The first history import stored WhatsApp calls as localized unsupported
// notes. They contain no remaining call direction or video metadata, but
// Seam-Chat installations that imported those notes can explicitly treat the
// history-pull records as calls without affecting unrelated unsupported UI.
export function parseLegacySeamCallContent(content) {
  if (
    typeof content !== 'string' ||
    !LEGACY_UNSUPPORTED_CALL_PATTERN.test(content) ||
    !HISTORY_PULL_PATTERN.test(content)
  ) {
    return null;
  }

  return {
    kind: 'legacy',
    video: false,
    duration: null,
  };
}
